import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail, MessageSquare, ShieldCheck, ArrowRight, MapPin } from "lucide-react";
import ogImageAsset from "../assets/og-image.jpg.asset.json";
import { STATES_DATA, slugify } from "@/data/states";

const TOP_METROS: Array<{ state: string; city: string }> = [
  { state: "california", city: "Los Angeles" },
  { state: "california", city: "San Francisco" },
  { state: "california", city: "San Diego" },
  { state: "new-york", city: "New York City" },
  { state: "texas", city: "Houston" },
  { state: "texas", city: "Dallas" },
  { state: "texas", city: "Austin" },
  { state: "florida", city: "Miami" },
  { state: "florida", city: "Orlando" },
  { state: "florida", city: "Tampa" },
  { state: "illinois", city: "Chicago" },
  { state: "pennsylvania", city: "Philadelphia" },
  { state: "arizona", city: "Phoenix" },
  { state: "georgia", city: "Atlanta" },
  { state: "washington", city: "Seattle" },
  { state: "massachusetts", city: "Boston" },
  { state: "colorado", city: "Denver" },
  { state: "nevada", city: "Las Vegas" },
  { state: "michigan", city: "Detroit" },
  { state: "ohio", city: "Columbus" },
];

import { getTurnstileSiteKey } from "@/lib/turnstile";
const TURNSTILE_ENV_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SITE_URL = "https://vendingmachinefree.com";
const OG_IMAGE = `${SITE_URL}${ogImageAsset.url}`;

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Us — Free Vending Machines USA" },
      {
        name: "description",
        content:
          "Contact Vending Machine Free.com for FREE vending machine inquiries across the USA. Full-service install, restock & maintenance. We try to reply in 4–6 hours.",
      },
      {
        name: "keywords",
        content:
          "contact vending machines, free vending machine enquiry, vending machine quote USA, vending placement contact",
      },
      { property: "og:title", content: "Contact Us — Free Vending Machines USA" },
      {
        property: "og:description",
        content:
          "Send your enquiry for free vending machine placement, install & service across the USA. We reply in 4–6 hours.",
      },
      { property: "og:url", content: `${SITE_URL}/contact` },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Us — Free Vending Machines USA" },
      {
        name: "twitter:description",
        content:
          "Send your enquiry for free vending machine placement & service across the USA. Reply in 4–6 hours.",
      },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/contact` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Vending Machine Free.com",
          url: `${SITE_URL}/contact`,
          description:
            "Contact form for free vending machine placement and service inquiries across the USA.",
          inLanguage: "en-US",
          isPartOf: { "@type": "WebSite", url: SITE_URL, name: "Vending Machine Free.com" },
          publisher: {
            "@type": "Organization",
            name: "Vending Machine Free.com",
            url: SITE_URL,
          },
          mainEntity: {
            "@type": "Organization",
            name: "Vending Machine Free.com",
            url: SITE_URL,
            areaServed: { "@type": "Country", name: "United States" },
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "customer service",
                areaServed: "US",
                availableLanguage: ["English"],
                url: `${SITE_URL}/contact`,
                hoursAvailable: "Mo-Fr 08:00-18:00",
                contactOption: "OnlineChat",
              },
              {
                "@type": "ContactPoint",
                contactType: "sales",
                areaServed: "US",
                availableLanguage: ["English"],
                url: `${SITE_URL}/contact`,
                contactOption: "OnlineChat",
              },
              {
                "@type": "ContactPoint",
                contactType: "technical support",
                areaServed: "US",
                availableLanguage: ["English"],
                url: `${SITE_URL}/contact`,
                contactOption: "OnlineChat",
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Vending Machine Free.com",
          url: SITE_URL,
          logo: `${SITE_URL}/favicon-512.png`,
          areaServed: { "@type": "Country", name: "United States" },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            areaServed: "US",
            availableLanguage: ["English"],
            url: `${SITE_URL}/contact`,
            contactOption: "OnlineChat",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Contact", item: `${SITE_URL}/contact` },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

type FieldErrors = Partial<Record<string, string>>;

function fireAnalytics(event: string, props: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as {
      dataLayer?: Array<Record<string, unknown>>;
      gtag?: (...args: unknown[]) => void;
      plausible?: (event: string, opts?: Record<string, unknown>) => void;
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, form_id: "contact", ...props });
    w.gtag?.("event", event, { event_category: "contact_form", ...props });
    w.plausible?.(event, { props: { form: "contact", ...props } });
  } catch {
    /* analytics is best-effort */
  }
}

function ContactPage() {
  useEffect(() => {
    fireAnalytics("contact_form_view");
  }, []);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formStartedAt] = useState(() => Date.now());
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [captchaStatus, setCaptchaStatus] = useState<
    "idle" | "loading" | "ready" | "expired" | "error"
  >(TURNSTILE_ENV_KEY ? "loading" : "idle");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | undefined>(TURNSTILE_ENV_KEY);
  useEffect(() => {
    setTurnstileSiteKey(getTurnstileSiteKey());
  }, []);


  useEffect(() => {
    if (typeof window === "undefined" || !TURNSTILE_ENV_KEY) return;
    const w = window as unknown as {
      __vmContactTurnstileCb?: (t: string) => void;
      __vmContactTurnstileExpired?: () => void;
      __vmContactTurnstileError?: () => void;
    };
    w.__vmContactTurnstileCb = (t: string) => {
      setCaptchaToken(t);
      setCaptchaStatus("ready");
      setFieldErrors((p) => ({ ...p, turnstile_token: undefined }));
    };
    w.__vmContactTurnstileExpired = () => {
      setCaptchaToken("");
      setCaptchaStatus("expired");
    };
    w.__vmContactTurnstileError = () => {
      setCaptchaToken("");
      setCaptchaStatus("error");
    };
    if (!document.querySelector('script[data-turnstile="1"]')) {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.dataset.turnstile = "1";
      s.onerror = () => setCaptchaStatus("error");
      document.head.appendChild(s);
    }
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    setCaptchaStatus("loading");
    const w = window as unknown as { turnstile?: { reset: (sel?: string) => void } };
    try {
      w.turnstile?.reset(".cf-turnstile");
    } catch {
      /* widget may not be ready */
    }
  };


  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);
    setFieldErrors({});

    const fd = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = Object.fromEntries(fd.entries());
    payload.form_started_at = formStartedAt;
    if (TURNSTILE_ENV_KEY) payload.turnstile_token = captchaToken;

    // Client-side checks
    const errs: FieldErrors = {};
    if (!String(payload.full_name || "").trim()) errs.full_name = "Please enter your name";
    if (!/^\S+@\S+\.\S+$/.test(String(payload.email || ""))) errs.email = "Enter a valid email";
    if (String(payload.message || "").trim().length < 10)
      errs.message = "Please include a short message (10+ chars)";
    if (TURNSTILE_ENV_KEY && !captchaToken) errs.turnstile_token = "Please complete the CAPTCHA";
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      fireAnalytics("form_validation_error", {
        form_id: "contact",
        form_location: "contact_page",
        error_type: "client_validation",
        invalid_fields: Object.keys(errs).join(","),
        field_count: Object.keys(errs).length,
      });
      return;
    }

    setSubmitting(true);
    fireAnalytics("contact_form_submit_attempt");
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        issues?: { fieldErrors?: Record<string, string[]> };
      };
      if (!res.ok || !json.ok) {
        // Rate limited — friendly, distinct message
        if (res.status === 429) {
          const retryAfter = Number(res.headers.get("Retry-After")) || 60;
          const msg = `Too many submissions from your network. Please wait ${retryAfter}s and try again.`;
          setFormError(msg);
          setSubmitting(false);
          fireAnalytics("contact_form_submit_error", { reason: "rate_limited", retry_after: retryAfter });
          return;
        }
        const fe: FieldErrors = {};
        const issues = json.issues?.fieldErrors;
        if (issues) {
          for (const [k, v] of Object.entries(issues)) {
            if (v && v.length) fe[k] = v[0];
          }
        }
        setFieldErrors(fe);
        // Turnstile-specific: reset widget so user can retry
        if (fe.turnstile_token) {
          try {
            const w = window as unknown as { turnstile?: { reset: (sel?: string) => void } };
            w.turnstile?.reset(".cf-turnstile");
          } catch { /* noop */ }
          setCaptchaToken("");
        }
        setFormError(json.error || "Submission failed. Please try again.");
        setSubmitting(false);
        fireAnalytics("contact_form_submit_error", { reason: json.error || "validation" });
        return;
      }
      fireAnalytics("contact_form_submit_success", {
        time_to_submit_ms: Date.now() - formStartedAt,
      });
      navigate({ to: "/thank-you" });
    } catch {
      setFormError("Network error. Please try again.");
      setSubmitting(false);
      fireAnalytics("contact_form_submit_error", { reason: "network" });
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/90 backdrop-blur sticky top-0 z-40">
        <div className="container-x flex h-16 md:h-20 items-center justify-between gap-3">
          <a href="/" className="font-display text-lg font-bold uppercase">
            Vending Machine Free.com
          </a>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground hover:opacity-95"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </header>

      <section
        className="text-steel-foreground"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="container-x py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <MessageSquare className="h-3.5 w-3.5" /> Contact · USA Nationwide
          </div>
          <h1 className="mt-4 font-display text-3xl md:text-5xl uppercase leading-tight">
            Contact Free Vending Machines
          </h1>
          <p className="mt-4 text-white/85 max-w-2xl mx-auto">
            Send us your enquiry about FREE vending machine placement, installation, or service
            anywhere in the USA. We try to respond in less than 4hrs – 6hrs.
          </p>
        </div>
      </section>

      <section className="container-x py-12 md:py-16 grid gap-10 md:grid-cols-5">
        <aside className="md:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-xl uppercase">How to reach us</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              The fastest way to reach our team is this form — it routes directly to our intake
              inbox and is monitored throughout US business hours.
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 text-safety" />
                <span>Inquiries are handled via the form on this page.</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 text-safety" />
                <span>Spam-protected with CAPTCHA & honeypot filtering.</span>
              </li>
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="font-display text-lg uppercase">What to include</h3>
            <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
              <li>Business name, address & state</li>
              <li>Number of employees / daily visitors</li>
              <li>Machine type (drink, snack, combo, coffee, food, ice)</li>
              <li>Any access / parking notes</li>
            </ul>
          </div>
        </aside>

        <form
          onSubmit={onSubmit}
          noValidate
          className="md:col-span-3 rounded-lg border border-border bg-card p-6 md:p-8 space-y-5"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <div>
            <label htmlFor="full_name" className="block text-sm font-semibold">
              Full name *
            </label>
            <input
              id="full_name"
              name="full_name"
              required
              maxLength={120}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-invalid={!!fieldErrors.full_name}
            />
            {fieldErrors.full_name && (
              <p className="mt-1 text-xs text-destructive">{fieldErrors.full_name}</p>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                maxLength={255}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                aria-invalid={!!fieldErrors.phone}
              />
              {fieldErrors.phone && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.phone}</p>
              )}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="business_name" className="block text-sm font-semibold">
                Business name
              </label>
              <input
                id="business_name"
                name="business_name"
                maxLength={160}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-semibold">
                State
              </label>
              <input
                id="state"
                name="state"
                maxLength={60}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold">
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={6}
              maxLength={4000}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              aria-invalid={!!fieldErrors.message}
              placeholder="Tell us about your location and what you're looking for…"
            />
            {fieldErrors.message && (
              <p className="mt-1 text-xs text-destructive">{fieldErrors.message}</p>
            )}
          </div>

          {TURNSTILE_ENV_KEY ? (
            <div>
              <div
                className="cf-turnstile"
                data-sitekey={turnstileSiteKey}
                data-callback="__vmContactTurnstileCb"
                data-expired-callback="__vmContactTurnstileExpired"
                data-error-callback="__vmContactTurnstileError"
                data-timeout-callback="__vmContactTurnstileExpired"
                data-theme="light"
              />
              {captchaStatus === "loading" && (
                <p className="mt-1 text-xs text-muted-foreground">Loading CAPTCHA…</p>
              )}
              {captchaStatus === "expired" && (
                <p className="mt-1 text-xs text-destructive">
                  CAPTCHA expired.{" "}
                  <button type="button" onClick={resetCaptcha} className="underline">
                    Retry
                  </button>
                </p>
              )}
              {captchaStatus === "error" && (
                <p className="mt-1 text-xs text-destructive">
                  CAPTCHA failed to load.{" "}
                  <button type="button" onClick={resetCaptcha} className="underline">
                    Try again
                  </button>
                </p>
              )}
              {fieldErrors.turnstile_token && captchaStatus === "ready" && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.turnstile_token}</p>
              )}
            </div>

          ) : (
            <p className="text-xs text-muted-foreground">
              Protected by spam filtering. Add a Cloudflare Turnstile site key
              (<code>VITE_TURNSTILE_SITE_KEY</code>) to enable the CAPTCHA widget.
            </p>
          )}

          {formError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 text-sm font-bold uppercase tracking-wide text-safety-foreground hover:opacity-95 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send Enquiry"} <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-xs text-muted-foreground">
            We try to respond in less than 4hrs – 6hrs.
          </p>
        </form>
      </section>

      <section className="py-14 bg-secondary border-t border-border">
        <div className="container-x">
          <h2 className="font-display text-2xl md:text-3xl uppercase">
            Apply from Any State — Nationwide FREE Vending
          </h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            We install and service across all 50 states. Jump to your state or top metro:
          </p>

          <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-safety">Top Metro Areas</h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {TOP_METROS.map((m) => (
              <Link
                key={`${m.state}-${m.city}`}
                to="/states/$state/$town"
                params={{ state: m.state, town: slugify(m.city) }}
                className="flex items-center gap-2 rounded-md bg-card border border-border px-3 py-2 text-sm hover:border-safety hover:text-safety transition"
              >
                <MapPin className="h-3.5 w-3.5 text-safety" />
                {m.city}
              </Link>
            ))}
          </div>

          <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-safety">All 50 States</h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {STATES_DATA.slice().sort((a, b) => a.name.localeCompare(b.name)).map((s) => (
              <Link
                key={s.code}
                to="/states/$state"
                params={{ state: s.slug }}
                className="rounded-md bg-card border border-border px-3 py-2 text-sm hover:border-safety hover:text-safety transition"
              >
                {s.name}
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/coverage" className="inline-flex items-center gap-2 rounded-md bg-safety px-5 py-2.5 text-sm font-semibold text-safety-foreground">
              View USA Coverage Map <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/sitemap" className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold hover:border-safety">
              Full Site Map
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
